import { Box, Button, IconButton, Paper, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useInventory, useStore, useUpdateInventoryStatus } from '@gozoki/api';
import { useNavigate, useParams } from 'react-router-dom';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { DashboardCard } from '../../utils/dashboard/dashboardCards';
import { DateTime } from 'luxon';
import { InventoryDetails } from './InventoryDetails';
import { InventoryGrid } from './InventoryGrid';
import Page from '../../components/Page';
import { ROUTE_BUILDER } from '../../utils/constants/routes.constants';
import { ThemeColors } from '../../utils/theme/colors.theme';
import { UniqueArticle } from '@gozoki/api-types';
import { downloadDataXlsx } from '../../utils/excel/downloadDataXlsx';
import { fonts } from '../../utils/theme/fonts.theme';
import { frenchFormat } from '@gozoki/tools';
import { useInventoryProductRows } from '../../utils/hooks/useInventoryProductRows';

const CANCEL_BUTTON_STATE = {
    validated: null,
    pending: { text: "Annuler l'inventaire", disabled: false },
    canceled: null,
};

export const InventoryDetailsPage = () => {
    const { id, storeId: storeId_ } = useParams();
    const navigate = useNavigate();

    const inventoryId = useMemo(() => Number.parseInt(id ?? '', 10), [id]);
    const storeId = useMemo(() => Number.parseInt(storeId_ ?? '', 10), [storeId_]);
    const { data: inventoryDetail, isLoading, refetch: fetchInventory } = useInventory(inventoryId);

    const [updatedInventoryDetail, setUpdatedInventoryDetail] = useState<boolean>(false);
    const { data: store } = useStore(storeId);
    const { mutate: updateInventoryStatus } = useUpdateInventoryStatus();

    const dateDateTime = useMemo(() => {
        return DateTime.fromISO(inventoryDetail?.createdAt ?? '');
    }, [inventoryDetail?.createdAt]);

    const productRows = useInventoryProductRows(inventoryDetail);

    const unScannedArticles = useMemo(() => {
        if (!inventoryDetail?.currentStock) {
            return [];
        }
        return inventoryDetail.currentStock.filter(
            (article) =>
                !inventoryDetail.incomingStock.some(
                    (newArticle) => article.checkpointArticleRef === newArticle.checkpointArticleRef
                )
        );
    }, [inventoryDetail]);

    const expiredArticles = useMemo(() => {
        if (!inventoryDetail?.currentStock) {
            return [];
        }
        return inventoryDetail.currentStock.filter((article) => {
            const inventoryDate = new Date(inventoryDetail.createdAt);
            const articleDlcDate = new Date(article.dlc);
            return articleDlcDate < inventoryDate;
        });
    }, [inventoryDetail]);

    const lostArticles = useMemo(() => {
        if (!unScannedArticles || unScannedArticles.length === 0) {
            return [] as UniqueArticle[];
        }

        return unScannedArticles.filter(
            (article) =>
                !expiredArticles.some(
                    (expiredArticle) =>
                        expiredArticle.checkpointArticleRef === article.checkpointArticleRef
                )
        );
    }, [expiredArticles, unScannedArticles]);

    const unScannableProductsCount = useMemo(() => {
        if (!inventoryDetail?.unScannableProducts?.unScannableProducts) {
            return 0;
        }
        return inventoryDetail.unScannableProducts.unScannableProducts.reduce(
            (acc, p) => acc + p.count,
            0
        );
    }, [inventoryDetail]);

    const navigateBack = useCallback(() => {
        navigate(ROUTE_BUILDER.seeStoreDetails(storeId), {
            state: { currentTab: 3 }, // Navigate to "INVENTAIRES PASSÉS" tab
        });
    }, [navigate, storeId]);

    const handleCancel = useCallback(
        () =>
            updateInventoryStatus(
                { id: inventoryId, status: 'canceled' },
                {
                    onSuccess: () => {
                        fetchInventory();
                        setUpdatedInventoryDetail(!updatedInventoryDetail);
                    },
                }
            ),
        [
            inventoryId,
            updatedInventoryDetail,
            fetchInventory,
            setUpdatedInventoryDetail,
            updateInventoryStatus,
        ]
    );

    const handleExportInventory = useCallback(() => {
        const exportData = productRows.map((row) => ({
            id: row.id,
            reference: row.reference,
            label: row.label,
            price: row.price,
            theoricalCount: row.theoricalCount,
            inventoryCount: row.inventoryCount,
            unscannedCount: row.unscannedCount,
            delta: row.delta,
        }));

        const columnWidths: number[] = [10, 20, 35, 15, 30, 12, 20, 10];
        const metadata = {
            title: `Inventaire du ${dateDateTime.toFormat('dd/MM/yyyy')}`,
            date: dateDateTime.toFormat('dd/MM/yyyy'),
            description: inventoryDetail?.comment ?? "Détails de l'inventaire",
        };

        downloadDataXlsx({
            data: exportData,
            columns: {
                id: 'ID',
                reference: 'Référence',
                label: 'Libellé',
                price: "Prix d'achat",
                theoricalCount: 'Stock théorique (avant livraison)',
                inventoryCount: 'Stock relevé',
                unscannedCount: 'Stock non scanné',
                delta: 'Delta',
            },
            label: `details_inventaire_${dateDateTime.toFormat('yyyy-MM-dd')}_${(
                store?.label ?? ''
            ).replaceAll(' ', '-')}`,
            columnWidths,
            metadata,
        });
    }, [inventoryDetail, productRows, store?.label, dateDateTime]);

    // Calculate delivered today items with fallback logic
    const deliveredTodayArticles = useMemo(
        () => inventoryDetail?.deliveredArticles ?? [],
        [inventoryDetail]
    );

    const receptionRows = useMemo(() => {
        // Group by product and count volumes
        const productMap = new Map<
            number,
            { id: number; reference: string; label: string; volume: number }
        >();

        deliveredTodayArticles.forEach((article) => {
            const existing = productMap.get(article.product.id);
            if (existing) {
                existing.volume += 1;
            } else {
                productMap.set(article.product.id, {
                    id: article.product.id,
                    reference: article.product.reference,
                    label: article.product.label,
                    volume: 1,
                });
            }
        });

        return Array.from(productMap.values());
    }, [deliveredTodayArticles]);

    const handleExportReception = useCallback(
        () =>
            downloadDataXlsx({
                data: receptionRows,
                columns: {
                    id: 'ID',
                    reference: 'Référence',
                    label: 'Libellé',
                    volume: 'Volume reçu',
                },
                label: `reception-${inventoryDetail?.label ?? 'inventaire'}`,
            }),
        [inventoryDetail, receptionRows]
    );

    const handleExportInventoryTable = useCallback(() => {
        const exportData = productRows.map((row) => ({
            reference: row.reference,
            label: row.label,
            theoricalCountBeforeDelivery: row.theoricalCount,
            theoricalCountAfterDelivery: row.theoricalCount + (row.reapproNeeds || 0),
            inventoryCount: row.inventoryCount + row.unscannedCount,
            delta: row.delta,
        }));

        downloadDataXlsx({
            data: exportData,
            columns: {
                reference: 'Référence',
                label: 'Libellé',
                theoricalCountBeforeDelivery: 'Stock théorique (avant livraison)',
                theoricalCountAfterDelivery: 'Stock théorique (après livraison)',
                inventoryCount: 'Stock relevé',
                delta: 'Delta',
            },
            label: `inventaire_${DateTime.fromISO(inventoryDetail?.createdAt ?? '').toFormat(
                'yyyy-MM-dd'
            )}_${(store?.label ?? '').replaceAll(' ', '-')}`,
            columnWidths: [15, 30, 35, 35, 15, 12],
            metadata: {
                title: `Inventaire du ${dateDateTime.toFormat('dd/MM/yyyy')}`,
                date: dateDateTime.toFormat('dd/MM/yyyy'),
                description:
                    inventoryDetail?.comment ??
                    'Comparaison entre le stock théorique et le stock relevé',
            },
        });
    }, [inventoryDetail, productRows, store?.label, dateDateTime]);

    if (!inventoryDetail) return null;

    return (
        <Page>
            <div
                style={{
                    display: 'flex',
                    paddingLeft: 20,
                    alignItems: 'center',
                    gap: 10,
                    paddingBottom: 40,
                }}
            >
                <IconButton onClick={navigateBack}>
                    <ArrowBackIcon
                        sx={{
                            color: ThemeColors.BLACK,
                            height: '24px',
                            width: '24px',
                        }}
                    />
                </IconButton>
                <Typography style={fonts.pageSubtitle} flexGrow={1}>
                    {store?.label} / {inventoryDetail.label}
                </Typography>
                <Box display="flex" gap={2}>
                    {CANCEL_BUTTON_STATE[inventoryDetail.status] ? (
                        <Button
                            variant="outlined"
                            color="error"
                            sx={{ height: '36px', bgcolor: 'white' }}
                            onClick={handleCancel}
                        >
                            {CANCEL_BUTTON_STATE[inventoryDetail.status]?.text}
                        </Button>
                    ) : (
                        <Button
                            variant="outlined"
                            color="success"
                            sx={{ height: '36px' }}
                            onClick={handleExportInventory}
                        >
                            Exporter inventaire
                        </Button>
                    )}
                    <Button
                        variant="outlined"
                        color="primary"
                        sx={{ height: '36px' }}
                        onClick={handleExportInventoryTable}
                    >
                        Exporter tableau
                    </Button>
                    {inventoryDetail.status === 'validated' && (
                        <Button
                            variant="outlined"
                            color="success"
                            sx={{ height: '36px' }}
                            onClick={handleExportReception}
                        >
                            Exporter réception
                        </Button>
                    )}
                    {inventoryDetail.status === 'canceled' && (
                        <Button variant="contained" disabled sx={{ height: '36px' }}>
                            Annulé
                        </Button>
                    )}
                </Box>
            </div>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    paddingBottom: 20,
                    ...fonts.roboto,
                }}
            >
                <DashboardCard
                    label="Total stock"
                    bigValue={`${frenchFormat(inventoryDetail.currentStock.length)}`}
                    orange
                    smallValue="Stock théorique avant inventaire"
                />
                <DashboardCard
                    label="Total scannés"
                    bigValue={`${frenchFormat(inventoryDetail.incomingStock.length)}`}
                    orange
                    smallValue="Articles scannés durant l'inventaire"
                />
                <DashboardCard
                    label="Total vol"
                    bigValue={`${frenchFormat(lostArticles.length)}`}
                    smallValue="Articles volés depuis le dernier inventaire"
                />
                <DashboardCard
                    label="Total casse"
                    bigValue={`${frenchFormat(expiredArticles.length)}`}
                    smallValue="Articles expirés ou détruits depuis le dernier inventaire"
                />
                <DashboardCard
                    label="Total livraison"
                    bigValue={`${frenchFormat(deliveredTodayArticles.length)}`}
                    smallValue="Articles livrés depuis le dernier inventaire"
                />
                <DashboardCard
                    label="Total non scannables"
                    bigValue={`${frenchFormat(unScannableProductsCount)}`}
                    smallValue="Articles en boutique mais non scannables, à inclure dans la casse"
                />
            </div>
            <div style={{ flex: 1 }}>
                <InventoryDetails
                    label={inventoryDetail.label || ''}
                    comment={inventoryDetail.comment || ''}
                    inventoryId={inventoryDetail.id}
                    onValidate={() => fetchInventory()}
                />
                <Paper>
                    <InventoryGrid isLoading={isLoading} productRows={productRows} />
                </Paper>
            </div>
        </Page>
    );
};
